import { useMemo } from 'react';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { typography, radius } from '@/shared/theme/tokens';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useLiveModeModel } from '../../hooks/useLiveModeModel';
import { HUD } from './hudTheme';
import { TelemetryStrip } from './TelemetryStrip';
import { Semaphore } from './Semaphore';
import { FurnaceStage } from './FurnaceStage';
import { ActionStack } from './ActionStack';
import { GaugeRing } from './GaugeRing';
import { GlassPanel, PanelHeader } from './GlassPanel';

// Presentation-only keyframes. Each use is gated on the model's `animate` flag
// (false under reduced-motion). Motion only ever means "this just changed".
const KEYFRAMES = `
@keyframes hud-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
@keyframes hud-glow { 0%, 100% { filter: brightness(1); } 50% { filter: brightness(1.18); } }
@keyframes hud-flash { 0% { opacity: 0.25; } 100% { opacity: 1; } }
@keyframes hud-fade-in { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: none; } }
`;

const GAUGE_MAX = 6;

// One gauge per parameter, in ranked order (stale already sunk to the bottom).
function pickGauges(alerts, max = GAUGE_MAX) {
  const byName = new Map();
  for (const a of alerts) if (!byName.has(a.parameterName)) byName.set(a.parameterName, a);
  return [...byName.values()].slice(0, max);
}

/**
 * Mission-control HUD — an aerospace command-center realization of Live Mode.
 * A dominant status semaphore (left) answers "does the AI need me?", the plant
 * schematic anchors the center as a glowing spatial reference with zone
 * callouts, the flight-director stack (right) holds the ranked directives, and a
 * gauge rail reads out the key parameters. Same model + IA as every variant;
 * only the visual language differs. Read-only — clicks open the page's drawer.
 */
export function HudScreen({ deploymentName, svgUrl, seedAlerts, seedRecs, isLoading, isError, onSelectRec, onSelectAlert }) {
  const { isDesktop } = useBreakpoint();
  const {
    alerts, recs, clock, flashId, jitterActive,
    degraded, rankedRecs, rankedAlerts,
    headline, freshness, animate,
  } = useLiveModeModel(seedAlerts, seedRecs);

  const gauges = useMemo(() => pickGauges(rankedAlerts), [rankedAlerts]);
  const booting = isLoading && alerts.length === 0 && recs.length === 0;

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, gap: 12,
      padding: 12, color: HUD.ink, fontFamily: typography.fontFamily.body,
      background: `radial-gradient(120% 80% at 50% -10%, ${HUD.panelRaised}, ${HUD.void} 60%)`,
    }}>
      <style>{KEYFRAMES}</style>

      <TelemetryStrip
        deploymentName={deploymentName}
        clock={clock}
        jitterActive={jitterActive}
        animate={animate}
        freshness={freshness}
      />

      {isError ? (
        <GlassPanel style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ErrorState message="SIGNAL LOST — couldn't acquire live telemetry for this deployment." />
        </GlassPanel>
      ) : booting ? (
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: isDesktop ? '320px 1fr 360px' : '1fr', gap: 12 }}>
          <GlassPanel style={{ padding: 16 }}><SkeletonCard rows={6} height="100%" /></GlassPanel>
          {isDesktop && <GlassPanel style={{ padding: 16 }}><SkeletonCard rows={8} height="100%" /></GlassPanel>}
          {isDesktop && <GlassPanel style={{ padding: 16 }}><SkeletonCard rows={6} height="100%" /></GlassPanel>}
        </div>
      ) : (
        <>
          {/* command row */}
          <div style={{
            flex: 1, minHeight: 0,
            display: isDesktop ? 'grid' : 'flex', flexDirection: isDesktop ? undefined : 'column',
            gridTemplateColumns: isDesktop ? '320px minmax(0, 1fr) 360px' : undefined, gap: 12,
          }}>
            <div style={{ display: 'flex', minHeight: isDesktop ? 0 : 320 }}>
              <Semaphore headline={headline} animate={animate} />
            </div>
            <div style={{ display: 'flex', minHeight: isDesktop ? 0 : 360 }}>
              <FurnaceStage
                svgUrl={svgUrl}
                alerts={rankedAlerts}
                flashId={flashId}
                animate={animate}
                onSelectAlert={onSelectAlert}
              />
            </div>
            <div style={{ display: 'flex', minHeight: isDesktop ? 0 : 320 }}>
              <ActionStack recs={rankedRecs} paused={degraded} animate={animate} onSelect={onSelectRec} />
            </div>
          </div>

          {/* gauge rail */}
          <GlassPanel accent={HUD.accent} style={{ flexShrink: 0 }}>
            <PanelHeader
              title="Parameter Telemetry"
              accent={HUD.accent}
              count={gauges.length}
              right={(
                <span style={{
                  fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.wide, color: HUD.inkFaint,
                }}>
                  VALUE · BAND · TREND
                </span>
              )}
            />
            {gauges.length === 0 ? (
              <div style={{
                padding: '22px 16px', textAlign: 'center', fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm, color: HUD.inkFaint, letterSpacing: typography.letterSpacing.wide,
              }}>
                NO PARAMETERS TRACKED
              </div>
            ) : (
              <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'stretch',
                padding: '4px 6px 8px',
              }}>
                {gauges.map((a, i) => (
                  <div key={a.id} style={{
                    flex: '1 1 150px', minWidth: 150, display: 'flex',
                    borderLeft: i === 0 ? 'none' : `1px solid ${HUD.strokeSoft}`,
                    borderRadius: radius.md,
                  }}>
                    <GaugeRing alert={a} flash={flashId === a.id} animate={animate} />
                  </div>
                ))}
              </div>
            )}
          </GlassPanel>
        </>
      )}
    </div>
  );
}
